"use client";

import Link from "next/link";
import { Fragment, type ReactNode } from "react";

import { TbMonogram } from "@/components/brand/tb-monogram";
import { PublicGiftListCard } from "@/components/gift-list-search/public-gift-list-card";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Icon } from "@/components/ui/icon";
import { LoadMore } from "@/components/ui/pagination";
import { PUBLIC_SEARCH_SERVER_ERROR_MESSAGE } from "@/lib/errors/error-messages";
import type {
  PublicGiftListItem,
  SearchCriteria,
} from "@/lib/gift-list-search/public-search-types";
import type { PublicSearchState } from "@/lib/gift-list-search/search-state";

/** Renders one search result inside the results grid. */
/** `index` is the result's place in the grid, from 0; a renderer that does not need it ignores it. */
export type RenderSearchResultItem = (item: PublicGiftListItem, index: number) => ReactNode;

interface GiftListSearchResultsProps {
  state: PublicSearchState;
  onLoadMore(): void;
  onRetry(): void;
  onExpandSearch(): void;
  onClear(): void;
  /**
   * Draws each result. The grid keys every entry by `item.id`, so the returned node needs
   * no key of its own. Defaults to the public guest card.
   */
  renderItem?: RenderSearchResultItem;
  /**
   * Last section of the empty state, inside the same section frame. Defaults to the public
   * guest note with the concierge contact; `null` drops the section.
   */
  emptyFooter?: ReactNode;
}

const renderPublicGiftListCard: RenderSearchResultItem = (item, index) => (
  <PublicGiftListCard item={item} position={index + 1} />
);

const PUBLIC_EMPTY_FOOTER: ReactNode = (
  <>
    <p>
      Se o nome não aparecer de jeito nenhum, a lista pode ainda não estar
      aberta. Uma consultora confirma isso com você.
    </p>
    <Button asChild variant="outline">
      <Link href="/#ajuda">Falar com a concierge</Link>
    </Button>
  </>
);

export function getPublicSearchStatus(state: PublicSearchState): string {
  switch (state.phase) {
    case "idle":
      return "";
    case "loading":
      return "Buscando listas.";
    case "results":
      return state.failedOperation === null
        ? "Resultados disponíveis."
        : "Não foi possível carregar mais resultados.";
    case "load-more":
      return "Carregando mais resultados.";
    case "empty":
      return "Nenhuma lista encontrada.";
    case "error":
      return "Não foi possível buscar listas.";
  }
}

function getSearchTerm(criteria: SearchCriteria): string {
  const query = criteria.query?.trim();
  return `“${query === undefined || query.length === 0 ? "sua busca" : query}”`;
}

function SearchSkeletons({ count = 3 }: { count?: number }) {
  return Array.from({ length: count }, (_, index) => (
    <div
      // Skeleton positions have no domain identity; the index is stable for this fixed reserved set.
      key={index}
      aria-hidden="true"
      className="tb-public-list-skeleton"
    >
      <span className="tb-skeleton-shimmer tb-skeleton-shimmer-on-light tb-public-list-skeleton-surface" />
    </div>
  ));
}

function ResultsHeader({
  count,
  criteria,
  empty = false,
  onClear,
}: {
  count: number;
  criteria: SearchCriteria;
  empty?: boolean;
  onClear(): void;
}) {
  const term = getSearchTerm(criteria);
  const countLabel =
    count === 1 ? "lista encontrada" : "listas encontradas";

  return (
    <div className="tb-public-search-results-header">
      <p>
        <b>{empty ? "Nenhuma lista" : count}</b>{" "}
        {!empty && countLabel} para{" "}
        <span className="tb-public-search-results-term">{term}</span>
      </p>
      <Button
        className="tb-public-search-clear"
        type="button"
        variant="link"
        onClick={onClear}
      >
        Limpar busca
      </Button>
    </div>
  );
}

function ResultsGrid({
  state,
  renderItem,
}: {
  state: Extract<PublicSearchState, { phase: "results" | "load-more" }>;
  renderItem: RenderSearchResultItem;
}) {
  return (
    <div className="tb-public-list-grid">
      {state.items.map((item, index) => (
        <Fragment key={item.id}>{renderItem(item, index)}</Fragment>
      ))}
      {state.phase === "load-more" && <SearchSkeletons />}
    </div>
  );
}

function EmptySearch({
  state,
  onClear,
  onExpandSearch,
  footer,
}: {
  state: Extract<PublicSearchState, { phase: "empty" }>;
  onClear(): void;
  onExpandSearch(): void;
  footer: ReactNode;
}) {
  const term = getSearchTerm(state.criteria);
  const preview = state.preview;
  const canExpand = preview.status === "ready" && preview.page.items.length > 0;
  const onlyName =
    state.criteria.query !== undefined &&
    state.criteria.eventDateFrom === undefined;
  const diagnostic = canExpand
    ? "Um dos filtros pode estar estreito demais. Solte um deles e a lista talvez apareça."
    : onlyName
      ? "Pode ser o nome escrito de outro jeito: tente o primeiro nome de um dos dois, ou o nome da lista."
      : "Revise o nome, a data ou a cidade: às vezes um só detalhe revela tudo.";

  return (
    <>
      <ResultsHeader
        count={0}
        criteria={state.criteria}
        empty
        onClear={onClear}
      />
      <EmptyState
        // The reference's title (`Nada encontrado para …`): clamp(23px → 30px), Intrinseca 300.
        // Set through the wrapper because the DS title's own utilities outrank the stylesheet.
        className="tb-public-search-empty [&>h3]:mb-3 [&>h3]:text-[clamp(23px,2.29vw,30px)] [&>h3]:font-light [&>h3]:leading-[1.14]"
        icon={<Icon name="search" />}
        title={
          <>
            Nada encontrado para{" "}
            <span className="tb-public-search-empty-term">{term}</span>
          </>
        }
        description={diagnostic}
      >
        {canExpand && (
          <div className="tb-public-search-empty-section tb-public-search-empty-broaden">
            <p className="tb-public-search-empty-eyebrow">Amplie a busca</p>
            <Button
              aria-label={`Buscar sem a data do evento ${preview.page.totalItems} ${
                preview.page.totalItems === 1 ? "lista" : "listas"
              }`}
              className="tb-public-search-expand"
              type="button"
              variant="outline"
              onClick={onExpandSearch}
            >
              <span>Buscar sem a data do evento</span>
              <span>
                {preview.page.totalItems}{" "}
                {preview.page.totalItems === 1 ? "lista" : "listas"}
              </span>
            </Button>
          </div>
        )}
        {footer !== null && (
          <div className="tb-public-search-empty-section">{footer}</div>
        )}
      </EmptyState>
    </>
  );
}

export function GiftListSearchResults({
  state,
  onLoadMore,
  onRetry,
  onExpandSearch,
  onClear,
  renderItem = renderPublicGiftListCard,
  emptyFooter = PUBLIC_EMPTY_FOOTER,
}: GiftListSearchResultsProps) {
  const busy =
    state.phase === "loading" ||
    state.phase === "load-more" ||
    (state.phase === "empty" && state.preview.status === "loading");
  let content: ReactNode = null;

  if (state.phase === "loading") {
    content = (
      <div className="tb-public-list-grid">
        <SearchSkeletons />
      </div>
    );
  }

  if (state.phase === "results" || state.phase === "load-more") {
    const unit = state.metadata.totalItems === 1 ? "lista" : "listas";
    content = (
      <>
        <ResultsHeader
          count={state.metadata.totalItems}
          criteria={state.criteria}
          onClear={onClear}
        />
        <ResultsGrid state={state} renderItem={renderItem} />
        <LoadMore
          aria-label="Paginação dos resultados"
          className="tb-public-search-pagination"
          shown={state.items.length}
          total={state.metadata.totalItems}
          unit={unit}
          done={!state.metadata.hasNextPage}
          loading={state.phase === "load-more"}
          loadingLabel="Carregando listas"
          loadingIndicator={
            <TbMonogram className="tb-public-search-loading-monogram" />
          }
          progressLabel="Progresso dos resultados"
          showAction={
            state.phase === "load-more" || state.failedOperation === null
          }
          onLoadMore={onLoadMore}
        />
        {state.phase === "results" && state.failedOperation !== null && (
          <div className="tb-public-search-page-error">
            <p>{PUBLIC_SEARCH_SERVER_ERROR_MESSAGE}</p>
            <Button type="button" variant="outline" onClick={onRetry}>
              Tentar novamente
            </Button>
          </div>
        )}
      </>
    );
  }

  if (state.phase === "empty") {
    content = (
      <EmptySearch
        state={state}
        onClear={onClear}
        onExpandSearch={onExpandSearch}
        footer={emptyFooter}
      />
    );
  }

  if (state.phase === "error") {
    content = (
      <EmptyState
        className="tb-public-search-empty"
        title="Não foi possível concluir a busca"
        description={PUBLIC_SEARCH_SERVER_ERROR_MESSAGE}
      >
        <Button type="button" variant="outline" onClick={onRetry}>
          Tentar novamente
        </Button>
      </EmptyState>
    );
  }

  return (
    <>
      <p aria-atomic="true" aria-live="polite" className="sr-only" role="status">
        {getPublicSearchStatus(state)}
      </p>
      <section
        aria-busy={busy}
        aria-label="Resultados da busca"
        className="tb-public-search-results"
        data-phase={state.phase}
      >
        {content}
      </section>
    </>
  );
}
